import React, { useMemo } from 'react';
import {
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isToday,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { ArrowPathIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { dayKey, eventsByDay, safeParse } from './calendarDateUtils';
import { colorForEvent, EVENT_COLOR_CLASSES } from './eventColors';
import type { CalendarEvent } from '../../types/integrations';

interface CalendarWeekViewProps {
  cursorDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
}

const WEEK_OPTIONS = { weekStartsOn: 1 as const };
const HOUR_HEIGHT = 48; // px per hour (each 30-min slot = 24px)
const DAY_HEIGHT = 24 * HOUR_HEIGHT;

interface PositionedEvent {
  event: CalendarEvent;
  top: number;
  height: number;
}

const positionEvent = (
  event: CalendarEvent,
  day: Date
): PositionedEvent | null => {
  if (event.all_day) return null;
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + 24 * 60 * 60 * 1000;
  const start = safeParse(event.start).getTime();
  const end = safeParse(event.end).getTime();
  // Clip to day window
  const visibleStart = Math.max(start, dayStart);
  const visibleEnd = Math.min(end, dayEnd);
  if (visibleEnd <= visibleStart) return null;
  const top = ((visibleStart - dayStart) / (60 * 60 * 1000)) * HOUR_HEIGHT;
  const height = Math.max(
    20,
    ((visibleEnd - visibleStart) / (60 * 60 * 1000)) * HOUR_HEIGHT - 2
  );
  return { event, top, height };
};

const CalendarWeekView: React.FC<CalendarWeekViewProps> = ({
  cursorDate,
  events,
  onEventClick,
  onDayClick,
}) => {
  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(cursorDate, WEEK_OPTIONS),
        end: endOfWeek(cursorDate, WEEK_OPTIONS),
      }),
    [cursorDate]
  );
  const eventMap = useMemo(() => eventsByDay(events), [events]);

  const allDayEventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const day of days) {
      const list = (eventMap.get(dayKey(day)) ?? []).filter((e) => e.all_day);
      if (list.length) map.set(dayKey(day), list);
    }
    return map;
  }, [days, eventMap]);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="flex flex-col h-[700px] overflow-hidden">
      {/* Day header row */}
      <div className="grid border-b border-border-primary/15" style={{ gridTemplateColumns: '60px repeat(7, minmax(0, 1fr))' }}>
        <div className="border-r border-border-primary/15" />
        {days.map((day) => (
          <div
            key={day.toISOString()}
            className="px-2 py-2 text-center border-r border-border-primary/15 last:border-r-0"
          >
            <div className="text-xs font-bold uppercase tracking-wider text-text-secondary">
              {format(day, 'EEE')}
            </div>
            <div
              className={[
                'mt-1 text-base font-semibold inline-flex items-center justify-center min-w-[28px] h-7 rounded-full',
                isToday(day) ? 'bg-error-500 text-white' : 'text-text-primary',
              ].join(' ')}
            >
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>

      {/* All-day row */}
      {allDayEventsByDay.size > 0 && (
        <div
          className="grid border-b border-border-primary/15 bg-surface-primary"
          style={{ gridTemplateColumns: '60px repeat(7, minmax(0, 1fr))' }}
        >
          <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-text-tertiary border-r border-border-primary/15 flex items-center">
            All day
          </div>
          {days.map((day) => {
            const list = allDayEventsByDay.get(dayKey(day)) ?? [];
            return (
              <div
                key={day.toISOString()}
                className="border-r border-border-primary/15 last:border-r-0 p-1 flex flex-col gap-0.5 min-h-[28px]"
              >
                {list.map((e) => {
                  const c = EVENT_COLOR_CLASSES[colorForEvent(e.id)];
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onEventClick?.(e);
                      }}
                      className={[
                        'text-xs font-medium px-2 py-1 rounded-1 truncate text-left active:scale-[0.97] transition-transform',
                        c.pill,
                        e.is_tereiko ? 'border-l-2 border-primary-500' : '',
                      ].join(' ')}
                    >
                      {e.summary || 'Untitled event'}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Time grid */}
      <div className="flex-1 overflow-y-auto">
        <div
          className="grid relative"
          style={{
            gridTemplateColumns: '60px repeat(7, minmax(0, 1fr))',
            height: DAY_HEIGHT,
          }}
        >
          {/* Time gutter */}
          <div className="border-r border-border-primary/15">
            {hours.map((h) => (
              <div
                key={h}
                style={{ height: HOUR_HEIGHT }}
                className="text-[11px] text-text-tertiary text-right pr-1.5 -translate-y-2"
              >
                {h === 0 ? '' : format(new Date(2000, 0, 1, h), 'h a').toLowerCase()}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day) => {
            const dayEvents = (eventMap.get(dayKey(day)) ?? []).filter(
              (e) => !e.all_day && isSameDay(safeParse(e.start), day)
            );
            const positioned = dayEvents
              .map((e) => positionEvent(e, day))
              .filter((p): p is PositionedEvent => p !== null);
            return (
              <div
                key={day.toISOString()}
                className={[
                  'relative border-r border-border-primary/15 last:border-r-0',
                  isToday(day) ? 'bg-primary-500/8' : '',
                ].join(' ')}
                onClick={() => onDayClick?.(day)}
              >
                {/* Hour grid lines */}
                {hours.map((h) => (
                  <div
                    key={h}
                    style={{ height: HOUR_HEIGHT }}
                    className="border-t border-border-primary/10 first:border-t-0"
                  />
                ))}
                {/* Events */}
                {positioned.map(({ event, top, height }) => {
                  const c = EVENT_COLOR_CLASSES[colorForEvent(event.id)];
                  return (
                    <button
                      key={event.id}
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onEventClick?.(event);
                      }}
                      style={{ top, height }}
                      className={[
                        'absolute left-1 right-1 rounded-1 px-2 py-1 text-xs font-medium text-left overflow-hidden',
                        'active:scale-[0.97] transition-transform shadow-sm',
                        c.pill,
                        event.is_tereiko ? 'border-l-2 border-primary-500' : '',
                      ].join(' ')}
                    >
                      <div className="font-bold text-[11px] flex items-center gap-1">
                        {format(safeParse(event.start), 'h:mma')
                          .toLowerCase()
                          .replace(/m$/, '')}
                        {event.is_recurring && <ArrowPathIcon className="w-2.5 h-2.5 opacity-70" />}
                        {event.meeting_link && <VideoCameraIcon className="w-2.5 h-2.5 opacity-70" />}
                      </div>
                      <div className="truncate">{event.summary || 'Untitled event'}</div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarWeekView;
